import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  createPotentialMerchant,
  getMerchantInvites,
  getPotentialMerchant,
  getReasons,
  merchantOnboard,
  saveMerchantData,
  sendFundraiserInvite,
  sendInvite,
  sendAdminInvite,
  getMerchantInviteByCode,
  getFundraiserInviteByCode,
  startOnboarding,
  createReason,
  fundraiserOnboard,
  getFundraiserResubmitApplication,
  onboardExistingMerchant,
  resubmitFundraiserApplication,
} from "../../services/onboardingService";
import toast from "../../utils/toast";

const initialState = {
  merchantInviteSent: null,
  allInvites: null,
  startedOnboarding: null,
  savedMerchant: null,
  potentialMerchant: null,
  createdMerchant: null,
  onboardedMerchant: null,
  fundraiserInviteSent: null,
  fundraiserInviteDetails: null,
  allReasons: null,
  reasonCreated: null,
  onboardedFundraiser: null,
  fundraiserResubmitApplication: null,
  fundraiserResubmittedApplication: null,
  loading: null,
  // Persist custom category locally during onboarding only
  pendingCustomCategory: "",
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState: initialState,
  reducers: {
    setPendingCustomCategory: (state, action) => {
      state.pendingCustomCategory = action?.payload || "";
    },
    clearPendingCustomCategory: (state) => {
      state.pendingCustomCategory = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendInvite?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.merchantInviteSent = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(sendAdminInvite?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.merchantInviteSent = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(getMerchantInviteByCode?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.merchantInviteDetails = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(getMerchantInvites?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.allInvites = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(startOnboarding?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.startedOnboarding = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(saveMerchantData?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.savedMerchant = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(getPotentialMerchant?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.potentialMerchant = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(onboardExistingMerchant?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        toast.success("Merchant Successfully Onboarded!", {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
        state.existingMerchantOnboard = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(createPotentialMerchant?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.createdMerchant = action?.payload?.data;
        toast.success("Merchant Successfully Onboarded!", {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(merchantOnboard?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.onboardedMerchant = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(sendFundraiserInvite?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.fundraiserInviteSent = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(getFundraiserInviteByCode?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.fundraiserInviteDetails = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(getReasons?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.allReasons = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(createReason?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.reasonCreated = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(fundraiserOnboard?.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.onboardedFundraiser = action?.payload?.data;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(
      getFundraiserResubmitApplication?.fulfilled,
      (state, action) => {
        if (action?.payload?.success === true) {
          state.fundraiserResubmitApplication = action?.payload?.data;
        } else {
          toast.error(action?.payload?.message);
        }
        state.loading = false;
      }
    );

    builder.addCase(
      resubmitFundraiserApplication?.fulfilled,
      (state, action) => {
        if (action?.payload?.success === true) {
          state.fundraiserResubmittedApplication = action?.payload?.data;
        } else {
          toast.error(action?.payload?.message);
        }
        state.loading = false;
      }
    );

    builder
      .addMatcher(
        isAnyOf(
          createPotentialMerchant?.pending,
          getMerchantInvites?.pending,
          getPotentialMerchant?.pending,
          merchantOnboard?.pending,
          saveMerchantData?.pending,
          sendInvite?.pending,
          sendAdminInvite?.pending,
          getMerchantInviteByCode?.pending,
          startOnboarding?.pending,
          sendFundraiserInvite?.pending,
          getFundraiserInviteByCode?.pending,
          getReasons?.pending,
          createReason?.pending,
          fundraiserOnboard?.pending,
          onboardExistingMerchant?.pending,
          getFundraiserResubmitApplication?.pending,
          resubmitFundraiserApplication?.pending
        ),
        (state) => {
          state.loading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          createPotentialMerchant?.rejected,
          getMerchantInvites?.rejected,
          getPotentialMerchant?.rejected,
          merchantOnboard?.rejected,
          saveMerchantData?.rejected,
          sendInvite?.rejected,
          sendAdminInvite?.rejected,
          getMerchantInviteByCode?.rejected,
          startOnboarding?.rejected,
          sendFundraiserInvite?.rejected,
          getFundraiserInviteByCode?.rejected,
          getReasons?.rejected,
          createReason?.rejected,
          fundraiserOnboard?.rejected,
          onboardExistingMerchant?.rejected,
          getFundraiserResubmitApplication?.rejected,
          resubmitFundraiserApplication?.rejected
        ),
        (state, action) => {
          state.loading = false;
          toast.error(action?.payload?.message);
        }
      );
  },
});

export const { setPendingCustomCategory, clearPendingCustomCategory } = onboardingSlice?.actions;
export default onboardingSlice?.reducer;
